const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'policyora_token';

function authHeaders(json = true): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (json) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

// ── Labels ──────────────────────────────────────────────────────────────

export const COMPLIANCE_DOMAIN_LABELS: Record<string, string> = {
  transparency: 'Transparency',
  data_governance: 'Data Governance',
  risk_management: 'Risk Management',
  human_oversight: 'Human Oversight',
  accountability: 'Accountability',
  security: 'Security',
  fairness: 'Fairness',
  explainability: 'Explainability',
  incident_reporting: 'Incident Reporting',
  conformity_assessment: 'Conformity Assessment',
};

export const COMPLIANCE_STATUS_LABELS: Record<string, string> = {
  compliant: 'Compliant',
  partial: 'Partial',
  non_compliant: 'Non-Compliant',
};

// ── Status / Session ────────────────────────────────────────────────────

export interface ComplianceStatus {
  session_id: string;
  status: string;
  current_node: string | null;
  trigger_reason?: string | null;
}

export async function getComplianceStatus(sessionId: string): Promise<ComplianceStatus> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/status/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<ComplianceStatus>(res);
}

// ── HITL question / answer / upload-doc (mirror governance) ─────────────

export interface ComplianceQuestion {
  question_id: string;
  element_id?: string;
  dimension_id?: string;
  policy_id: string;
  question_text: string;
  answer_text?: string | null;
  question_type?: 'document_request' | 'standalone_question' | null;
  document_type?: string | null;
  document_description?: string | null;
  resolves_question_ids?: string[] | null;
  resolves_dimension_ids?: string[] | null;
  obligation_id?: string | null;
}

export async function getComplianceQuestions(sessionId: string): Promise<ComplianceQuestion[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/questions/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<ComplianceQuestion[]>(res);
}

export async function submitComplianceAnswers(
  sessionId: string,
  answers: { question_id: string; answer_text: string }[],
): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/answers/${sessionId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ answers }),
  });
  return parseResponse<{ status: string }>(res);
}

export async function uploadComplianceDocument(
  sessionId: string,
  questionId: string,
  file: File,
): Promise<{ status: string; question_id: string; s3_key: string; filename: string }> {
  const form = new FormData();
  form.append('question_id', questionId);
  form.append('file', file);
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE_URL}/api/v1/compliance/upload-document/${sessionId}`,
    { method: 'POST', headers, body: form },
  );
  return parseResponse(res);
}

// ── Result types ────────────────────────────────────────────────────────

export interface DomainScoreDetail {
  score: number;
  compliant: number;
  partial: number;
  non_compliant: number;
  total: number;
  label?: string;
}

export interface PolicyScoreDetail {
  score: number;
  compliant: number;
  partial: number;
  non_compliant: number;
  total: number;
  policy_name?: string;
}

export interface ApplicabilityEntry {
  policy_id: string;
  policy_name?: string | null;
  article_id: string;
  article_heading?: string | null;
  applicable: boolean;
  applicability_reason: string | null;
  exemptions: string[] | null;
  rule_based?: boolean;
  computed_at?: string | null;
}

export interface ObligationEntry {
  obligation_id: string;
  policy_id: string;
  policy_element_id?: string | null;
  article_id: string;
  description: string;
  responsible_party: string | null;
  deadline: string | null;
  evidence_type: string | null;
  severity: string;
  domain: string;
  weight?: number;
  evidence_criteria?: string[];
  from_cache?: boolean;
}

export interface ObligationStatusEntry {
  obligation_id: string;
  policy_id?: string | null;
  article_id?: string | null;
  description?: string | null;
  domain: string | null;
  severity: string | null;
  status: 'compliant' | 'partial' | 'non_compliant';
  reason: string | null;
  gap: string | null;
  remediation: string | null;
  evidence_refs: string[] | null;
  responsible_party?: string | null;
  evidence_type?: string | null;
  deadline?: string | null;
  score_contribution?: number;
  last_checked_at?: string | null;
}

export interface RemediationItem {
  priority: number;
  obligationId: string;
  policyName: string;
  articleReference: string;
  gapDescription: string;
  recommendedAction: string;
  estimatedEffort: string;
  deadlineSuggestion: string;
  // Recency of the source policy this remediation is based on (ISO date, may be null).
  policyLastUpdated?: string | null;
}

export interface RemediationPlan {
  company_id?: string;
  session_id?: string;
  items: RemediationItem[];
  total_items: number;
  generated_at?: string;
}

export interface MergeStats {
  raw_count: number;
  post_dedup_count: number;
  post_filter_count?: number;
  deferred_count?: number;
  hard_excluded_count?: number;
  per_domain_counts?: Record<string, number>;
  deferred_by_domain?: Record<string, number>;
}

export interface ComplianceFinalResult {
  company_id: string;
  session_id: string;
  engine: string;
  overall_score: number;
  health_rating: string;
  per_policy_scores: Record<string, PolicyScoreDetail>;
  per_domain_scores: Record<string, DomainScoreDetail>;
  obligations_compliant: number;
  obligations_partial: number;
  obligations_non_compliant: number;
  total_obligations: number;
  applicability_results?: ApplicabilityEntry[];
  obligations?: ObligationEntry[];
  obligation_statuses: ObligationStatusEntry[];
  remediation_plan: RemediationPlan;
  critical_gaps?: ObligationStatusEntry[];
  high_gaps?: ObligationStatusEntry[];
  merge_stats?: MergeStats;
  deferred_count?: number;
  hitl_iterations?: number;
  assessed_at: string;
}

// ── Results / latest-result ─────────────────────────────────────────────

export interface AnswerFile {
  id: number;
  file_s3_key: string | null;
  file_name: string | null;
  file_size_bytes: number | null;
  content_type: string | null;
}

export interface AnswerVersion {
  version_id: number;
  version: number;
  answer_text: string | null;
  created_at: string | null;
  files: AnswerFile[];
}

export interface ComplianceLatestResultQuestion {
  id: number;
  question_id: string;
  dimension_id: string | null;
  policy_id: string | null;
  question_text: string;
  question_type: string | null;
  latest_answer: AnswerVersion | null;
  answer_versions: AnswerVersion[];
}

export interface ComplianceDimensionDetail {
  dimension_id: string;
  area: string;
  question: string;
  status: string;
  score_contribution: number | null;
  severity: string | null;
  policy_reference: string | null;
  company_evidence_summary: string | null;
  reasoning: string | null;
  gap: string | null;
  recommendation: string | null;
  evidence_used: string[] | null;
  doc_sources: string[] | null;
  source_policy_id: string | null;
  source_policy_name: string | null;
}

export interface ComplianceLatestResult {
  assessment_id: string | null;
  version: number | null;
  session_id: string;
  jurisdiction: string | null;
  assessed_at: string | null;
  final_result: ComplianceFinalResult;
  questions: ComplianceLatestResultQuestion[];
  dimension_details: Record<string, ComplianceDimensionDetail>;
}

export async function getComplianceResults(
  sessionId: string,
): Promise<ComplianceFinalResult | { status: string; current_node: string | null }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/results/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<ComplianceFinalResult | { status: string; current_node: string | null }>(res);
}

export async function getComplianceLatestResult(): Promise<ComplianceLatestResult> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/latest-result`, {
    headers: authHeaders(),
  });
  return parseResponse<ComplianceLatestResult>(res);
}

// ── Assessment history (governance-shaped) ──────────────────────────────

export interface ComplianceAssessmentSummary {
  assessment_id: string | null;
  session_id: string;
  version: number | null;
  jurisdiction: string | null;
  overall_score: number | null;
  health_rating: string | null;
  status: string;
  current_node: string | null;
  total_regulations: number | null;
  total_dimensions?: number | null;
  dimensions_met?: number | null;
  dimensions_partial?: number | null;
  dimensions_missing?: number | null;
  coverage_score?: number | null;
  hitl_iterations?: number | null;
  trigger_reason?: string | null;
  assessed_at: string | null;
  created_at: string | null;
}

export interface ComplianceAssessmentDetail {
  assessment_id: string;
  version: number;
  session_id: string;
  jurisdiction: string | null;
  overall_score: number | null;
  health_rating: string | null;
  status: string;
  assessed_at: string | null;
  final_result: ComplianceFinalResult | null;
  questions: ComplianceLatestResultQuestion[];
  dimension_details: Record<string, ComplianceDimensionDetail>;
}

export async function listComplianceAssessments(): Promise<ComplianceAssessmentSummary[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/assessments`, {
    headers: authHeaders(),
  });
  return parseResponse<ComplianceAssessmentSummary[]>(res);
}

export async function getComplianceAssessmentDetail(
  assessmentId: string,
): Promise<ComplianceAssessmentDetail> {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/compliance/assessments/${assessmentId}`,
    { headers: authHeaders() },
  );
  return parseResponse<ComplianceAssessmentDetail>(res);
}

// ── Score snapshot ──────────────────────────────────────────────────────

export interface ComplianceScoreSnapshot {
  company_id: string;
  session_id: string | null;
  overall_score: number | null;
  health_rating: string | null;
  per_policy_scores: Record<string, PolicyScoreDetail>;
  per_domain_scores: Record<string, DomainScoreDetail>;
  obligations_compliant: number;
  obligations_partial: number;
  obligations_non_compliant: number;
  total_obligations: number;
  last_computed_at: string | null;
}

export async function getLatestComplianceScore(): Promise<ComplianceScoreSnapshot> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/score`, { headers: authHeaders() });
  return parseResponse<ComplianceScoreSnapshot>(res);
}

// ── Obligations / applicability / remediation ──────────────────────────

export async function listObligations(statusFilter?: string): Promise<ObligationStatusEntry[]> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/obligations${qs}`, {
    headers: authHeaders(),
  });
  return parseResponse<ObligationStatusEntry[]>(res);
}

export async function getLatestRemediation(): Promise<RemediationPlan> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/remediation`, {
    headers: authHeaders(),
  });
  return parseResponse<RemediationPlan>(res);
}

export async function listApplicability(
  policyId?: string,
  applicableOnly = false,
): Promise<ApplicabilityEntry[]> {
  const params = new URLSearchParams();
  if (policyId) params.set('policy_id', policyId);
  if (applicableOnly) params.set('applicable_only', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/applicability${qs}`, {
    headers: authHeaders(),
  });
  return parseResponse<ApplicabilityEntry[]>(res);
}

// ── Evidence store CRUD ─────────────────────────────────────────────────

export interface ControlItem {
  controlId: string;
  name: string;
  description?: string | null;
  implementedDate?: string | null;
  evidenceUrl?: string | null;
  verifiedBy?: string | null;
}

export interface CertificationItem {
  name: string;
  issuingBody?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  documentUrl?: string | null;
}

export interface PolicyItem {
  name: string;
  type?: string | null;
  uploadedAt?: string | null;
  documentUrl?: string | null;
}

export interface AuditItem {
  date: string;
  auditor?: string | null;
  scope?: string | null;
  outcome?: string | null;
  reportUrl?: string | null;
}

export interface IncidentItem {
  date: string;
  description: string;
  severity?: string | null;
  resolution?: string | null;
}

export interface AssessmentResponseItem {
  questionId: string;
  question: string;
  answer: string;
  answeredAt?: string | null;
}

export interface EvidenceStore {
  controlsImplemented: ControlItem[];
  certificationsHeld: CertificationItem[];
  policiesAndDocuments: PolicyItem[];
  auditHistory: AuditItem[];
  incidentLog: IncidentItem[];
  assessmentResponses: AssessmentResponseItem[];
  lastCheckedAt?: Record<string, string>;
}

export async function getEvidenceStore(): Promise<EvidenceStore> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/evidence`, { headers: authHeaders() });
  return parseResponse<EvidenceStore>(res);
}

export async function replaceEvidenceStore(store: EvidenceStore): Promise<EvidenceStore> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/evidence`, {
    method: 'PUT', headers: authHeaders(), body: JSON.stringify(store),
  });
  return parseResponse<EvidenceStore>(res);
}

// ── Recomputation trigger ──────────────────────────────────────────────

export type ComplianceTriggerReason =
  | 'profile_update'
  | 'evidence_update'
  | 'policy_update'
  | 'manual';

export async function triggerRecompute(
  triggerReason: ComplianceTriggerReason,
  policyIds?: string[],
): Promise<{ status: string; trigger_reason: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/compliance/triggers/recompute`, {
    method: 'POST', headers: authHeaders(),
    body: JSON.stringify({ trigger_reason: triggerReason, policy_ids: policyIds ?? null }),
  });
  return parseResponse<{ status: string; trigger_reason: string }>(res);
}
